import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  loginWithEmailAndPasswordFirebase,
  loginWithGoogleFirebase,
  sendUserEmailVerification,
  sendUserPasswordResetEmail,
  signOutFirebase,
  signUpWithEmailAndPasswordFirebase,
} from "@/services/auth";
import { User, UserRegistration } from "@/models/models";
import { getUserInfo, loadOrCreateUserInfo } from "@/services/users";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useToastContext } from "@/context/ToastContext";
import { useAnalytics } from "@/context/GoogleAnalyticsContext";
import httpInstance from "@/services/httpClient";
import { auth } from "@/lib/firebaseClient";
import { onAuthStateChanged } from "firebase/auth";

export enum AuthOption {
  Login = "login",
  SignUp = "signup",
  Reset = "reset",
}

const USER = "user";

export interface IAuthContext {
  authLoading: boolean;
  authOption: AuthOption;
  setAuthOption: (authOption: AuthOption) => void;
  user: User;
  loadUser: (email: string) => void;
  registerWithEmailAndPassword: (user: UserRegistration) => Promise<void>;
  loginWithEmailAndPassword: (user: UserRegistration) => Promise<any>;
  loginWithGoogle: () => Promise<any>;
  loginWithMicrosoft: () => Promise<any>;
  connectMinecraft: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const defaultState = {
  authLoading: false,
  authOption: AuthOption.Login,
  setAuthOption: () => { },
  user: {} as User,
  loadUser: async () => { },
  registerWithEmailAndPassword: async () => { },
  loginWithEmailAndPassword: async () => { },
  loginWithGoogle: async () => { },
  loginWithMicrosoft: async () => { },
  connectMinecraft: async () => { },
  logout: async () => { },
  resetPassword: async () => { },
};

const AuthContext = createContext<IAuthContext>(defaultState);

const AuthProvider = (props: { children: ReactNode }) => {
  const router = useRouter();
  const { showErrorAlert } = useToastContext();
  const { setAnalyticsUserId } = useAnalytics();

  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [authOption, setAuthOption] = useState<AuthOption>(AuthOption.Login);
  const [user, setUser] = useState<any>({});
  const [localUser, setLocalUser] = useLocalStorage(USER, {} as User);

  useEffect(() => {
    let hasInitialized = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!hasInitialized && !firebaseUser) {
        hasInitialized = true;
        await new Promise(resolve => setTimeout(resolve, 500));
        const currentUser = auth.currentUser;

        if (currentUser && currentUser.email) {
          try {
            await loadUserData(currentUser.email);
          } catch (error) {
            console.error("[AuthContext] Failed to load user data:");
          } finally {
            setAuthLoading(false);
          }
          return;
        }
      }

      hasInitialized = true;

      if (firebaseUser) {
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const userEmail = firebaseUser.email || idTokenResult.claims.email as string;

        if (userEmail) {
          try {
            await loadUserData(userEmail);
          } catch (error) {
            console.error("[AuthContext] Failed to load user data:");
          } finally {
            setAuthLoading(false);
          }
        } else {
          setAuthLoading(false);
        }
      } else {
        setUser({});
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && user.email) {
      setLocalUser(user);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.email) {
      const analyticsUserId = user.xbox_user_id || user.minecraft_uuid || user.email;
      setAnalyticsUserId(analyticsUserId);
    } else {
      setAnalyticsUserId(null);
    }
  }, [user, setAnalyticsUserId]);

  const registerWithEmailAndPassword = (
    user: UserRegistration
  ): Promise<void> => {
    return signUpWithEmailAndPasswordFirebase(user)
      .then(async (response) => {
        await loadOrCreateMorpheusUser({ ...response, displayName: user.name });
        return sendUserEmailVerification(response);
      })
      .catch((error) => {
        showErrorAlert(error.message);
        return Promise.reject(error);
      });
  };

  const loginWithEmailAndPassword = (user: UserRegistration): Promise<User> => {
    return new Promise((resolve, reject) => {
      loginWithEmailAndPasswordFirebase(user)
        .then(async (response: any) => {
          await loadOrCreateMorpheusUser(response);
          resolve(response);
        })
        .catch((error) => {
          showErrorAlert(error.message);
          reject(error);
        });
    });
  };

  const loginWithGoogle = (): Promise<void> => {
    sessionStorage.setItem('socialAuthInProgress', 'true');

    return new Promise((resolve, reject) => {
      loginWithGoogleFirebase()
        .then(async (response) => {
          try {
            await loadOrCreateMorpheusUser(response.user);
            window.location.href = "/generator";
            resolve();
          } catch (error: any) {
            sessionStorage.removeItem('socialAuthInProgress');
            await signOutFirebase();
            showErrorAlert(error.message || "Failed to sign in with Google");
            reject(error);
          }
        })
        .catch((error) => {
          sessionStorage.removeItem('socialAuthInProgress');
          showErrorAlert(error.message);
          reject(error);
        });
    });
  };

  const loginWithMicrosoft = (): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await httpInstance.get("/minecraft/auth/oauth-url");
        if (response.data.success && response.data.oauth_url) {
          window.location.href = response.data.oauth_url;
          resolve();
        } else {
          showErrorAlert("Failed to initiate Microsoft login");
          reject(new Error("Failed to get OAuth URL"));
        }
      } catch (error: any) {
        showErrorAlert(error.message || "Failed to initiate Microsoft login");
        reject(error);
      }
    });
  };

  const loadOrCreateMorpheusUser = (firebaseUser: any, isMsOAuth: boolean = false): Promise<void> => {
    const user = {
      name: firebaseUser.displayName,
      email: firebaseUser.email,
      phone: firebaseUser.phoneNumber,
      ms_oauth_provider: isMsOAuth,
    };
    if (!user.email) {
      showErrorAlert("Email is required");
      return Promise.reject(new Error("Email is required"));
    }
    return loadOrCreateUser(user);
  };

  const loadOrCreateUser = (user: any): Promise<void> => {
    const newData = { ...user, role: USER };
    return loadOrCreateUserInfo(newData)
      .then((response: any) => {
        if (response.success) {
          setUser(response.data);
        } else {
          showErrorAlert(
            response.error || "An error occurred while loading the user data"
          );
          return Promise.reject(new Error(response.error || "An error occurred while loading the user data"));
        }
      })
      .catch((error) => {
        showErrorAlert("An error occurred while loading the user data");
        return Promise.reject(error);
      });
  };

  const loadUserData = (email: string) => {
    return getUserInfo(email)
      .then((response: any) => {
        if (response.success) {
          setUser(response.data);
          setLocalUser(response.data);
        }
      })
      .catch(() => {
        // Silently handle error - user data loading failed
      });
  };

  const logout = () => {
    return signOutFirebase()
      .then(() => {

        setUser({} as User);
        setLocalUser({} as User);
        setAnalyticsUserId(null);
        localStorage.removeItem(USER);
        localStorage.removeItem("results");

        return new Promise<void>((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (!firebaseUser) {
              unsubscribe();
              window.location.href = "/welcome";
              resolve();
            }
          });
        });
      })
      .catch((error) => {
        showErrorAlert(error.message);
      });
  };

  const resetPassword = (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      sendUserPasswordResetEmail(email).then(resolve).catch(reject);
    });
  };

  const connectMinecraft = (): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        if (!user.email) {
          showErrorAlert("Please log in first");
          reject(new Error("User not logged in"));
          return;
        }

        const response = await httpInstance.get(`/minecraft/auth/start?user_email=${encodeURIComponent(user.email)}`);

        if (response.data.success && response.data.oauth_url) {
          window.location.href = response.data.oauth_url;
          resolve();
        } else {
          showErrorAlert("Failed to initiate Minecraft authentication");
          reject(new Error("Failed to get OAuth URL"));
        }
      } catch (error: any) {
        showErrorAlert(error.message || "Failed to connect Minecraft");
        reject(error);
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        authLoading,
        authOption,
        setAuthOption,
        user,
        loadUser: loadUserData,
        registerWithEmailAndPassword,
        loginWithEmailAndPassword,
        loginWithGoogle,
        loginWithMicrosoft,
        connectMinecraft,
        logout,
        resetPassword,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
